import React, { useState, useRef, useEffect } from "react";
import { NavLink, useHistory, Link } from "react-router-dom";
import { URLS } from "src/_config";
import SiteLogo from "./SiteLogo";
import { useTranslation } from "src/_common/hooks/useTranslation";
import {
  useAppUserDetailsSelector,
  useAppUserPermissionsSelector,
  useAppUserDataSelector,
} from "src/_common/hooks/selectors/userSelector";
import { useAppUserAction } from "src/_common/hooks/actions/user/appUserActionHook";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import UpgradePlanModal from "../modal/UpgradePlanModal";
import NotificationPanel from "../modal/NotificationPanel";
import ExperienceRatingModal from "../modal/ExperienceRatingModal";
import { hasPermission } from "src/_common/utils/permissionsUtils";

interface SettingsSidebarProps {
  isCollapsed: boolean;
  onBackToDashboard: () => void;
}

function SettingsSidebar({ isCollapsed: propIsCollapsed, onBackToDashboard }: SettingsSidebarProps) {
  const { t } = useTranslation("sidebar");
  const history = useHistory();
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  const userData = useAppUserDataSelector();
  const userAction = useAppUserAction();
  const userApi = useUserApi();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(propIsCollapsed);
  const [showCreatorPopup, setShowCreatorPopup] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState<boolean>(false);
  const [showExperienceRatingModal, setShowExperienceRatingModal] = useState<boolean>(false);
  const creatorPopupRef = useRef<HTMLDivElement>(null);

  const toggleSidebarCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Helper function to check if email is verified
  const isEmailVerified = (): boolean => {
    return userDetails?.user?.is_verified === true;
  };

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member" && menuName != "security") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // If email is not verified, prevent access to settings
    if (!isEmailVerified()) {
      return;
    }
    setShowCreatorPopup(false);
    // Settings click logic can be added here
  };

  const handleCompanyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // If email is not verified, prevent access to company profile
    if (!isEmailVerified()) {
      return;
    }
    // For members with permission, navigate to view profile with company view
    if (userDetails?.user_type === "member" && canAccessMenu("edit_company")) {
      setShowCreatorPopup(false);
      history.push(`${URLS.AUTH.VIEW_PROFILE}?view=company`);
    } else if (userDetails?.user_type !== "member") {
      // For non-members, also navigate to company view
      setShowCreatorPopup(false);
      history.push(`${URLS.AUTH.VIEW_PROFILE}?view=company`);
    }
    // Members without permission - do nothing (no navigation)
  };

  const logOut = (e: React.MouseEvent) => {
    e.preventDefault();
    userApi.callLogout(
      (message: string, resp: any) => {
        // API logout successful
        history.push(URLS.AUTH.LOGIN);
      },
      (message: string) => {
        // API logout failed, still clear local state
        userAction.logout();
        history.push(URLS.AUTH.LOGIN);
      },
    );
  };

  // Handle click outside creator popup to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (creatorPopupRef.current && !creatorPopupRef.current.contains(event.target as Node)) {
        setShowCreatorPopup(false);
      }
    };

    if (showCreatorPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCreatorPopup]);

  return (
    <section className={`${isCollapsed ? "top-nav sidebar-wrap-collapse" : "top-nav "}`}>
      <nav
        id="settings-sidebar"
        className={`mCustomScrollbar _mCS_1 mCS-autoHide mCS_no_scrollbar settings-sidebar ${
          isCollapsed ? "sidebar-collapsed" : ""
        }`}
        style={{ overflow: "visible" }}
      >
        <div id="dismiss" onClick={toggleSidebarCollapse}>
          <i className={`fa ${isCollapsed ? "fa-arrow-right" : "fa-arrow-left"}`} />
        </div>
        {!isCollapsed ? (
          <div className="sidebar-header">
            <SiteLogo />
          </div>
        ) : (
          <div className="sidebar-header">
            <SiteLogo isCollapsed={isCollapsed} />
          </div>
        )}

        <ul
          className={`list-unstyled components settings-menu ${isCollapsed ? "icons-only" : ""}`}
        >
          {/* Back to Dashboard */}
          <li className="back-to-dashboard">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onBackToDashboard();
              }}
            >
              <i className="fa fa-arrow-left"></i>
              <span className="nav-text">{t("sidebar.settingsSidebar.backToMainMenu")}</span>
            </a>
          </li>

          {/* Settings Menu Items */}

          {/* {canAccessMenu('settings') && (
          <li>
            <NavLink to="/settings/general" activeClassName="active">
              <i className="fa fa-user sidebar-icon"></i>
              <span className="nav-text">General</span>
            </NavLink>
          </li>
        )} */}

          {canAccessMenu("read_members") && (
            <li className={!isEmailVerified() ? "disabled" : ""}>
              <NavLink
                to={URLS.MEMBERS.LIST}
                activeClassName="active"
                onClick={(e) => {
                  if (!isEmailVerified()) {
                    e.preventDefault();
                  }
                }}
                style={{
                  opacity: !isEmailVerified() ? 0.5 : 1,
                  cursor: !isEmailVerified() ? "not-allowed" : "pointer",
                  pointerEvents: !isEmailVerified() ? "none" : "auto",
                }}
              >
                <i className="fa fa-users sidebar-icon"></i>
                <span className="nav-text">{t("sidebar.settingsSidebar.menuItems.members")}</span>
              </NavLink>
            </li>
          )}

          {canAccessMenu("read_subscriptions") && (
            <li className={!isEmailVerified() ? "disabled" : ""}>
              <NavLink
                to="/settings/subscriptions"
                activeClassName="active"
                onClick={(e) => {
                  if (!isEmailVerified()) {
                    e.preventDefault();
                  }
                }}
                style={{
                  opacity: !isEmailVerified() ? 0.5 : 1,
                  cursor: !isEmailVerified() ? "not-allowed" : "pointer",
                  pointerEvents: !isEmailVerified() ? "none" : "auto",
                }}
              >
                <i className="fa fa-refresh sidebar-icon"></i>
                <span className="nav-text">{t("sidebar.settingsSidebar.menuItems.subscriptions")}</span>
              </NavLink>
            </li>
          )}

          {canAccessMenu("read_billing") && (
            <li className={!isEmailVerified() ? "disabled" : ""}>
              <NavLink
                to="/settings/billing"
                activeClassName="active"
                onClick={(e) => {
                  if (!isEmailVerified()) {
                    e.preventDefault();
                  }
                }}
                style={{
                  opacity: !isEmailVerified() ? 0.5 : 1,
                  cursor: !isEmailVerified() ? "not-allowed" : "pointer",
                  pointerEvents: !isEmailVerified() ? "none" : "auto",
                }}
              >
                <i className="fa fa-credit-card sidebar-icon"></i>
                <span className="nav-text">{t("sidebar.settingsSidebar.menuItems.billing")}</span>
              </NavLink>
            </li>
          )}

          {canAccessMenu("read_history") && (
            <li className={!isEmailVerified() ? "disabled" : ""}>
              <NavLink
                to="/settings/history"
                activeClassName="active"
                onClick={(e) => {
                  if (!isEmailVerified()) {
                    e.preventDefault();
                  }
                }}
                style={{
                  opacity: !isEmailVerified() ? 0.5 : 1,
                  cursor: !isEmailVerified() ? "not-allowed" : "pointer",
                  pointerEvents: !isEmailVerified() ? "none" : "auto",
                }}
              >
                <i className="fa fa-history sidebar-icon"></i>
                <span className="nav-text">{t("sidebar.settingsSidebar.menuItems.history")}</span>
              </NavLink>
            </li>
          )}

          {canAccessMenu("security") && (
            <li className={!isEmailVerified() ? "disabled" : ""}>
              <NavLink
                to="/settings/security"
                activeClassName="active"
                onClick={(e) => {
                  if (!isEmailVerified()) {
                    e.preventDefault();
                  }
                }}
                style={{
                  opacity: !isEmailVerified() ? 0.5 : 1,
                  cursor: !isEmailVerified() ? "not-allowed" : "pointer",
                  pointerEvents: !isEmailVerified() ? "none" : "auto",
                }}
              >
                <i className="fa fa-shield sidebar-icon"></i>
                <span className="nav-text">{t("sidebar.settingsSidebar.menuItems.security")}</span>
              </NavLink>
            </li>
          )}
        </ul>
        {/* Pinned items above creator badge */}
        <div className="sidebar-pinned-bottom">
          {canAccessMenu("read_subscriptions") && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowUpgradeModal(true);
              }}
              data-tooltip="Plans & Pricing"
            >
              <i className="sidebar-icon upgrade-plan-nav-icon"></i>
              <span className="nav-text">{t("sidebar.creatorBadge.upgradePlan")}</span>
            </a>
          )}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowNotificationPanel(true);
            }}
            className={showNotificationPanel ? "active" : ""}
          >
            <i className="sidebar-icon notification-nav-icon"></i>
            <span className="nav-text">{t("sidebar.settingsSidebar.menuItems.notifications")}</span>
            {userData?.notifications?.unread_count > 0 && (
              <span className="notification-badge">{userData?.notifications?.unread_count}</span>
            )}
          </a>
        </div>
        {/* Creator Badge for Settings */}
        <div className="drjob-creator-badge">
          <div className="creator-trigger" onClick={() => setShowCreatorPopup(!showCreatorPopup)}>
            <img src={(userDetails?.company as any)?.logo_url || "/images/company-avatar.jpg"} alt="Dr Job FZ LLC" />
            <div className="creator-info">
              <span className="creator-name">{(userDetails?.company as any)?.name}</span>
              <span className="creator-count">
                <i className="creator-icon"></i> 1 {t("sidebar.creatorBadge.creator")}
              </span>
            </div>
            <span className="creator-arrow">{showCreatorPopup ? "▲" : "▼"}</span>
          </div>

          {/* Creator Popup */}
          {showCreatorPopup && (
            <div className="creator-popup" ref={creatorPopupRef}>
              {/* Company Info - Only show for members */}

              <div
                className="popup-section"
                onClick={handleCompanyClick}
                style={{
                  cursor:
                    !isEmailVerified() || (userDetails?.user_type === "member" && !canAccessMenu("edit_company"))
                      ? "not-allowed"
                      : "pointer",
                  opacity: !isEmailVerified() ? 0.5 : 1,
                }}
              >
                <h4>{t("sidebar.creatorBadge.companyInfo")}</h4>
                {isEmailVerified() &&
                (userDetails?.user_type !== "member" ||
                  (userDetails?.user_type === "member" && canAccessMenu("edit_company"))) ? (
                  <Link
                    to={`${URLS.AUTH.VIEW_PROFILE}?view=company`}
                    className="company-profile"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCreatorPopup(false);
                    }}
                  >
                    <img
                      src={(userDetails?.company as any)?.logo_url || "/images/company-avatar.jpg"}
                      alt="Dr Job FZ LLC"
                    />
                    <div className="company-details">
                      <h6>{userDetails?.company?.name}</h6>
                      {/* <p>{userDetails?.user_type != "member" && (userDetails?.user as any)?.email}</p> */}
                    </div>
                  </Link>
                ) : (
                  <div className="company-profile">
                    <img
                      src={(userDetails?.company as any)?.logo_url || "/images/company-avatar.jpg"}
                      alt="Dr Job FZ LLC"
                    />
                    <div className="company-details">
                      <h6>{userDetails?.company?.name}</h6>
                      {/* <p>{userDetails?.user_type != "member" && (userDetails?.user as any)?.email}</p> */}
                    </div>
                  </div>
                )}
              </div>
              {/* Personal Info */}
              <div className="popup-section">
                <h4>{t("sidebar.creatorBadge.personalInfo")}</h4>
                <Link
                  to={
                    userDetails?.user_type !== "member"
                      ? `${URLS.AUTH.VIEW_PROFILE}?view=personal`
                      : URLS.AUTH.VIEW_PROFILE
                  }
                  className="user-profile"
                  onClick={() => {
                    setShowCreatorPopup(false);
                  }}
                >
                  <img
                    src={
                      (userDetails?.user_type == "member"
                        ? userDetails?.user?.profile_photo_url
                        : (userDetails as any)?.user?.profile_image_url) || "/images/user-avatar.jpg"
                    }
                  />
                  <div className="user-details">
                    <h5>{userDetails?.user_type === "member" ? userDetails?.user?.name : "My Profile"}</h5>
                    <p>{userDetails?.user?.email}</p>
                  </div>
                </Link>
              </div>

              {/* )} */}

              {/* Menu Options */}
              <div className="popup-menu">
                {canAccessMenu("read_subscriptions") && (
                  <a
                    href="#"
                    className="popup-menu-item upgrade"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowUpgradeModal(true);
                    }}
                    // Allow upgrade even if not verified
                    style={{}}
                    data-tooltip="Plans & Pricing"
                  >
                    <span className="upgrade-icon"></span>
                    <span>{t("sidebar.creatorBadge.upgradePlan")}</span>
                  </a>
                )}
                <a
                  href="#"
                  className={`popup-menu-item ${!isEmailVerified() ? "disabled" : ""}`}
                  onClick={handleSettingsClick}
                  style={{
                    opacity: !isEmailVerified() ? 0.5 : 1,
                    cursor: !isEmailVerified() ? "not-allowed" : "pointer",
                    pointerEvents: !isEmailVerified() ? "none" : "auto",
                  }}
                >
                  <span className="settings-icon"></span>
                  <span>{t("sidebar.creatorBadge.settings")}</span>
                </a>
                <a
                  href="#"
                  className="popup-menu-item"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowExperienceRatingModal(true);
                  }}
                >
                  <span className="share-feedback-icon"></span>
                  <span>{t("sidebar.creatorBadge.shareFeedback")}</span>
                </a>
                {/* {URLS.SUPPORT.CONTACT_US} */}
                <a
                  href="https://support.drjobpro.com/hc/en-us"
                  className="popup-menu-item"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="support-icon"></span>
                  <span>{t("sidebar.creatorBadge.support")}</span>
                </a>
                <a
                  href="#"
                  className="popup-menu-item logout"
                  onClick={(e) => {
                    e.preventDefault();
                    logOut(e);
                  }}
                >
                  <span className="log-out-icon"></span>
                  <span>{t("sidebar.creatorBadge.logOut")}</span>
                </a>
              </div>
            </div>
          )}
        </div>
        {/* Upgrade Plan Modal */}
        <UpgradePlanModal isVisible={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
        {/* Notification Panel */}
        <NotificationPanel isOpen={showNotificationPanel} onClose={() => setShowNotificationPanel(false)} />
        {/* Experience Rating Modal */}
        <ExperienceRatingModal isOpen={showExperienceRatingModal} onClose={() => setShowExperienceRatingModal(false)} />
      </nav>
    </section>
  );
}

export default SettingsSidebar;
